import { FieldTemplateType, type ParticipantFieldGroupTemplate } from '@piccolohealth/pbs-common';
import type { SelectOption } from '@piccolohealth/ui';
import { ChakraV3 } from '@piccolohealth/ui';
import React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { FaTrash } from 'react-icons/fa';
import { FormStack } from '../../../components/forms/FormStack';
import { HookedFormField } from '../../../components/forms/HookedFormField';
import { HookedInput } from '../../../components/forms/HookedInput';
import { HookedSelect } from '../../../components/forms/HookedSelect';
import { fieldTemplateTypeTagColorName } from '../../../components/generic/FieldTemplateTypeTag';

const HookedInputAttributesForm = () => {
  return (
    <FormStack>
      <HookedFormField name='attributes.prefix' label='Prefix'>
        <HookedInput name='attributes.prefix' placeholder='Enter a prefix' />
      </HookedFormField>

      <HookedFormField name='attributes.suffix' label='Suffix'>
        <HookedInput name='attributes.suffix' placeholder='Enter a suffix' />
      </HookedFormField>
    </FormStack>
  );
};

const HookedSelectAttributesForm = () => {
  const methods = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'attributes.options',
    shouldUnregister: true,
  });

  const options = fields as unknown as SelectOption<string>[];

  const onDeleteOption = React.useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );

  const onAddOption = React.useCallback(() => {
    append({
      label: 'Example',
      value: 'Example',
      raw: 'Example',
    });
  }, [append]);

  return (
    <HookedFormField name='attributes.options' label='Options'>
      <FormStack>
        {options.map((option, index) => (
          <ChakraV3.HStack key={`${option.value}-${index}`} gap={4}>
            <HookedInput name={`attributes.options.${index}.value`} size='sm' />
            <ChakraV3.IconButton
              onClick={() => onDeleteOption(index)}
              aria-label='Delete'
              size='xs'
              variant='ghost'
            >
              <FaTrash />
            </ChakraV3.IconButton>
          </ChakraV3.HStack>
        ))}

        <ChakraV3.Button onClick={() => onAddOption()} size='sm' variant='subtle'>
          Add new option
        </ChakraV3.Button>
      </FormStack>
    </HookedFormField>
  );
};

const HookedAttributesForm = () => {
  const type = useWatch({ name: 'type' }) as FieldTemplateType;

  switch (type) {
    case FieldTemplateType.Input:
      return <HookedInputAttributesForm />;
    case FieldTemplateType.Textarea:
    case FieldTemplateType.Datepicker:
      return null;
    case FieldTemplateType.Select:
    case FieldTemplateType.MultiSelect:
      return <HookedSelectAttributesForm />;
  }
};

interface Props {
  fieldGroupTemplates: ParticipantFieldGroupTemplate[];
}

export const ParticipantFieldTemplateForm = (props: Props) => {
  const { fieldGroupTemplates } = props;

  return (
    <FormStack>
      <HookedFormField name='name' label='Name'>
        <HookedInput name='name' size='sm' placeholder='Enter a name' />
      </HookedFormField>

      <HookedFormField name='type' label='Type'>
        <HookedSelect
          name='type'
          size='sm'
          optionVariant='tag'
          placeholder='Choose a type'
          options={Object.values(FieldTemplateType).map((type) => {
            const [color, label] = fieldTemplateTypeTagColorName(type);
            return {
              value: type,
              raw: type,
              label,
              color,
            };
          })}
        />
      </HookedFormField>

      <HookedFormField name='participantFieldGroupTemplateId' label='Group'>
        <HookedSelect
          name='participantFieldGroupTemplateId'
          size='sm'
          placeholder='Choose a group'
          options={fieldGroupTemplates.map((group) => {
            return {
              value: group.id,
              raw: group,
              label: group.name,
            };
          })}
        />
      </HookedFormField>

      <HookedAttributesForm />
    </FormStack>
  );
};
